/**
 * usePlatformStats — real usage counters for the landing hero.
 *
 * Baked numbers (last hand-run census) render on first paint so the page
 * never blanks and crawlers always see figures; the live nightly counts from
 * GET /platform-stats quietly replace them ~1.5s after load. Any failure —
 * endpoint off, server booting, garbage payload — keeps the baked numbers
 * with no error surface.
 *
 * BUNDLE RULE: this runs in the landing entry, so it must NOT import
 * src/api/api.jsx (axios + interceptors). The base-URL logic below mirrors
 * api.jsx exactly — same env var names (VITE_API_BASE_URL / VITE_BASE_URL),
 * same prod/local fallbacks. If api.jsx changes its resolution, change this
 * too. (The curatedImageMap localhost leak came from NOT matching api.jsx —
 * don't repeat it.)
 */
import { useEffect, useState } from 'react';

/** Census 2026-09-08, from the live /platform-stats compute itself. */
export const BAKED_STATS = { pages: 13760, papers: 2562, subjects: 13 };

/**
 * When the baked figures were counted. Shown to the visitor when the live
 * fetch does not land, so the page says "our last full count, 8 Sep 2026"
 * instead of claiming numbers are live and nightly when they are neither.
 *
 * UPDATE THIS WHENEVER BAKED_STATS CHANGES — a stale date under fresh numbers
 * is the same dishonesty in the other direction.
 */
export const BAKED_AS_OF = '2026-09-08';

const FETCH_DELAY_MS = 1500; // after LCP, off the critical path

const resolveBase = () => {
  const envBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_URL;
  return envBase ?? (import.meta.env.PROD ? 'https://test.unchay.in/' : 'http://localhost:8000/');
};

const isCount = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

/**
 * @returns {{pages:number, papers:number, subjects:number, isLive:boolean, asOf:string|null}}
 *   `isLive` is false until real numbers arrive. The caller MUST use it to
 *   decide what to claim about the figures — see the note below.
 */
export default function usePlatformStats() {
  // isLive false means "these are the baked census numbers". The hero's caption
  // reads it and says "our last full count, <date>" instead of "live from our
  // database, updated nightly".
  //
  // WHY THAT MATTERS (2026-09-11): the nightly job had never once succeeded in
  // production — a case-only import path that resolved on macOS and threw on
  // the Linux server, swallowed by a catch — so /platform-stats answered 503
  // from the day it shipped. The page showed the baked figures under a caption
  // promising live, nightly numbers. The fallback was right; the claim on top
  // of it was not. Never let this hook's consumer assert freshness it cannot
  // see.
  const [stats, setStats] = useState({ ...BAKED_STATS, isLive: false, asOf: BAKED_AS_OF });

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${resolveBase()}platform-stats`);
        // 503 + code STATS_NOT_COMPUTED is the server's normal "nothing counted
        // yet" answer, not a fault. Either way the baked numbers stand and the
        // caption stays honest, so there is nothing to distinguish here.
        if (!res.ok) return;
        const body = await res.json();
        const d = body?.data;
        if (!body?.success || !isCount(d?.pages) || !isCount(d?.papers) || !isCount(d?.subjects)) return;
        if (!cancelled) {
          setStats({
            pages: d.pages, papers: d.papers, subjects: d.subjects,
            isLive: true, asOf: d.computedAt ?? null,
          });
        }
      } catch {
        // Endpoint unreachable — the baked numbers stand, labelled as a census.
      }
    }, FETCH_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  return stats;
}
